import React from 'react';
import { useForm, useFormState } from 'react-hook-form';

import { Message } from '@components';
import {
  FormFieldset,
  SelectField,
  TextField,
  ToggleField,
} from '@Forms/fields.jsx';
import { urlFormat } from '@Forms/formatters.js';
import { errorsFromMutate } from '@Forms/utils.js';
import { ButtonSpinnerIcon } from '@svgs';

const parseFormattedURL = (formattedURL) => {
  // take a full URL and then parse into formatted_domain, base_directory and https
  // for use with our API.
  //
  // domain validation is handled by the backend/API
  const urlObj = new URL(formattedURL);

  return {
    formatted_domain: urlObj.hostname,
    base_directory: urlObj.pathname,
    https: urlObj.protocol == 'https:' ? true : false,
  };
};

export const SiteSettingsForm = ({ disabled, mutation, siteData }) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setError,
  } = useForm({
    defaultValues: siteData,
    mode: 'onBlur',
  });
  const { isDirty, isValid } = useFormState({ control });

  const onSubmit = (data) => {
    // parse the formatted_url into its parts and update the payload
    const parsed = parseFormattedURL(data.formatted_url);
    data = {
      ...data,
      ...parsed,
    };
    mutation.mutate(data, {
      onError: (error) => {
        // handle server-side validations and errors
        const errorsList = errorsFromMutate(error);
        errorsList
          .filter(({ field }) => field !== '__all__')
          .forEach(({ field, error }) => {
            if (['base_directory', 'formatted_domain'].includes(field)) {
              // apply errors from these fields to the shared formatted_url field
              field = 'formatted_url';
            }
            setError(field, error);
          });
      },
    });
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className={
        `sb-manage-content-form ` + (disabled ? 'sb-form-disabled' : '')
      }
    >
      <FormFieldset>
        <TextField
          register={register}
          error={errors.formatted_url}
          options={{ onChange: urlFormat }}
          name="formatted_url"
          label="Published URL"
          placeholder="https://www.example.com/yoursite/"
          helpText="Primary URL for this Website and its sub-pages"
        />
        <ToggleField
          register={register}
          control={control}
          error={errors.published}
          name="published"
          label="Published"
          helpText="If the domain DNS is live, should the Website load under the Published URL"
        />
      </FormFieldset>
      <FormFieldset legend="Advanced">
        <SelectField
          register={register}
          error={errors.type}
          options={{ required: 'This field is required' }}
          name="type"
          label="Change website type"
          helpText="Use with caution"
        >
          <option value="">Choose a type...</option>
          <option value="default">Standard website</option>
          <option value="template">Parent template</option>
        </SelectField>
      </FormFieldset>
      <div className="sb-manage-content-button-bar">
        <div className="sb-flex">
          {/*<button className="sb-btn-cancel sb-btn" onClick={resetForm}>Cancel</button>*/}
          <button
            type="submit"
            className="sb-btn sb-btn-save"
            disabled={disabled}
          >
            {mutation.isPending ? 'Publishing...' : 'Publish changes'}
            {mutation.isPending && <ButtonSpinnerIcon />}
          </button>
        </div>
        {!isValid && isDirty && (
          <Message messageStyle="error" title="Please review your form" />
        )}
        {mutation.isError && (
          <Message
            messageStyle="error"
            title="Error saving changes"
            message="Please review your form"
          />
        )}
        {mutation.isSuccess && (
          <Message messageStyle="success" title="Successfully saved" />
        )}
      </div>
    </form>
  );
};
